import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * True on Apple platforms, where the accelerator is ⌘ rather than Ctrl. Read
 * once at module load; a keyboard layout does not change mid-session, and this
 * keeps the hint and the handler agreeing with each other.
 *
 * No SSR guard: every component in this package touches the DOM, so there is
 * no context where nexus loads without a navigator, and a defensive branch
 * here would be permanently untestable.
 */
export const IS_APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/.test(navigator.platform)

/**
 * What to print in a keyboard hint for the search accelerator. A function
 * rather than a constant so both platforms are reachable from a test — a
 * module-scope ternary only ever evaluates the branch the test runner is on.
 */
export function searchShortcutHint(isApple: boolean = IS_APPLE_PLATFORM): string {
  return isApple ? '⌘K' : 'Ctrl K'
}

export function useSearchOverlay() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)

  const openSearch = useCallback(() => {
    setOpen(true)
  }, [])

  const closeSearch = useCallback(() => {
    setOpen(false)
    setQuery('')
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd/Ctrl+K fires from anywhere, a typing field included. That is the
      // whole reason it exists alongside "/": people reach for it mid-sentence,
      // and a shortcut that silently does nothing while focus is in a search
      // box is worse than no shortcut.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        if (!open) openSearch()
        return
      }
      if (e.key === '/' && !open) {
        const el = e.target as HTMLElement
        const tag = el.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return
        e.preventDefault()
        openSearch()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, openSearch])

  return { open, query, setQuery, openSearch, closeSearch, triggerRef }
}
